import React, { useState, useEffect, useRef } from 'react';
import proptypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import {
  FormController,
  FormLabel,
  Dropdown,
  MenuItem,
  TextField,
  Accordion,
  AccordionHeader,
  AccordionBody,
  Typography,
} from '@one-thd/sui-atomic-components';
import {
  DatePicker
} from '@one-thd/sui-date-pickers';
import {
  validateProjectName,
  validatePropertyType,
  validateProjectDescription,
  validateEstimatedStartDate,
  validateEstimatedEndDate,
  validatePhoneNumber
} from '../helpers';
import {
  EMPTY_PROJECT_NAME_MESSAGE
} from '../constants';
import { JobsiteInformationForm } from './JobsiteInformationForm';

const { bool, func, string, shape } = proptypes;

export const ProjectInformationForm = ({
  showErrors,
  projectInformation,
  setProjectInformation,
  isProjectInfoError,
  setIsProjectInfoError,
  deliveryPreferences,
  setDeliveryPreferences,
  contacts,
  setContacts,
  open,
  duplicateNameError,
  hasTradeCreditAttached,
  jobsiteInformation, 
  setJobsiteInformation,
  isJobsiteError, 
  setIsJobsiteError,
}) => {
  const projectNameRef = useRef(null);
  const propertyTypeRef = useRef(null);
  const estimatedEndDateRef = useRef(null);
  const notesRef = useRef(null);
  const siteConditionsRef = useRef(null);
  const deliveryInstructionsRef = useRef(null);
  const nameRef = useRef(null);
  const phoneRef = useRef(null);

  const [errors, setErrors] = useState({
    projectNameError: '',
    propertyTypeError: '',
    estimatedEndDateError: '',
    notesError: '',
    siteConditionsError: '',
    deliveryInstructionsError: '',
    phoneError: '',
    nameError: ''
  });
  const [expandedAccordion, setExpandedAccordion] = useState(false);
  const findFirstTruthyKey = (obj) => {
    return Object.keys(obj).find((key) => obj[key]) || null;
  };

  useEffect(() => {
    const firstErrorKey = findFirstTruthyKey(errors);

    if (showErrors && firstErrorKey) {
      const errorRefMap = {
        projectNameError: projectNameRef,
        propertyTypeError: propertyTypeRef,
        estimatedEndDateError: estimatedEndDateRef,
        notesError: notesRef,
        siteConditionsError: siteConditionsRef,
        deliveryInstructionsError: deliveryInstructionsRef,
        nameError: nameRef,
        phoneError: phoneRef
      };

      const targetRef = errorRefMap[firstErrorKey];
      if (targetRef?.current) {
        targetRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
      }
    }
  }, [showErrors, errors]);

  // Safely access form data with default values
  const {
    projectName = '',
    propertyType = '',
    estimatedStartDate = '',
    estimatedEndDate = '',
    notes = ''
  } = projectInformation || {};

  const {
    siteConditions = '',
    deliveryInstructions = ''
  } = deliveryPreferences || {};

  const {
    name = '',
    phone = ''
  } = contacts || {};

  const [propertyTypeDropdownValue, setPropertyTypeDropdownValue] = useState('');

  useEffect(() => {
    if (propertyType) {
      // eslint-disable-next-line max-len
      if (propertyType !== 'Single Family' && propertyType !== 'Multi-Family' && propertyType !== 'Renovation/Remodel') {
        setPropertyTypeDropdownValue('Other');
      } else {
        setPropertyTypeDropdownValue(propertyType);
      }
    }
  }, []);

  const {
    projectNameError,
    propertyTypeError,
    estimatedEndDateError,
    notesError,
    siteConditionsError,
    deliveryInstructionsError,
    phoneError,
    nameError
  } = errors;

  const handleNameValidation = (value) => {
    const { isError, errorMessage } = validateProjectName(value);
    if (isError) {
      setErrors((prevState) => ({
        ...prevState,
        projectNameError: errorMessage
      }));
    } else {
      setErrors((prevState) => ({
        ...prevState,
        projectNameError: ''
      }));
    }
  };

  const handleTypeValidation = (value) => {
    if (value === 'Other') {
      setErrors((prevState) => ({
        ...prevState,
        propertyTypeError: 'Please provide a Project Type'
      }));
    } else {
      setErrors((prevState) => ({
        ...prevState,
        propertyTypeError: ''
      }));
    }
  };

  const handleOtherTypeValidation = (value) => {
    const { isError, errorMessage } = validatePropertyType(value);
    if (isError) {
      setErrors((prevState) => ({
        ...prevState,
        propertyTypeError: errorMessage
      }));
    } else {
      setErrors((prevState) => ({
        ...prevState,
        propertyTypeError: ''
      }));
    }
  };

  const handleChangeAccordion = (event, isExpanded) => { 
        setExpandedAccordion(isExpanded); 
  };

  const handleEstimatedStartDateValidation = (value) => {
    const { isError, endDateErrorMessage } = validateEstimatedStartDate(value, estimatedEndDate);
    if (isError) {
      setErrors((prevState) => ({
        ...prevState,
        estimatedEndDateError: endDateErrorMessage
      }));
    } else {
      setErrors((prevState) => ({
        ...prevState,
        estimatedEndDateError: ''
      }));
    }
  };

  const handleEstimatedEndDateValidation = (value) => {
    const { isError, endDateErrorMessage } = validateEstimatedEndDate(value, estimatedStartDate);
    if (isError) {
      setErrors((prevState) => ({
        ...prevState,
        estimatedEndDateError: endDateErrorMessage
      }));
    } else {
      setErrors((prevState) => ({
        ...prevState,
        estimatedEndDateError: ''
      }));
    }
  };

  const handleNotesValidation = (value) => {
    const { isError, errorMessage } = validateProjectDescription(value, 200);
    if (isError) {
      setErrors((prevState) => ({
        ...prevState,
        notesError: errorMessage
      }));
    } else {
      setErrors((prevState) => ({
        ...prevState,
        notesError: ''
      }));
    }
  };

  const handleSiteConditionsValidation = (value) => {
    const { isError, errorMessage } = validateProjectDescription(value, 'Job Site Conditions');
    if (isError) {
      setErrors((prevState) => ({
        ...prevState,
        siteConditionsError: errorMessage
      }));
    } else {
      setErrors((prevState) => ({
        ...prevState,
        siteConditionsError: ''
      }));
    }
  };

  const handleDeliveryInstructionsValidation = (value) => {
    const { isError, errorMessage } = validateProjectDescription(value, 'Delivery Requirements');
    if (isError) {
      setErrors((prevState) => ({
        ...prevState,
        deliveryInstructionsError: errorMessage
      }));
    } else {
      setErrors((prevState) => ({
        ...prevState,
        deliveryInstructionsError: ''
      }));
    }
  };

  const handleContactNameValidation = (value) => {
    const { isError, errorMessage } = validateProjectDescription(value, 'Delivery Contact Name');
    if (isError) {
      setErrors((prevState) => ({
        ...prevState,
        nameError: errorMessage
      }));
    } else {
      setErrors((prevState) => ({
        ...prevState,
        nameError: ''
      }));
    }
  };

  const handlePhoneValidation = (value) => {
    const { isError, errorMessage } = validatePhoneNumber(value);
    if (isError) {
      setErrors((prevState) => ({
        ...prevState,
        phoneError: errorMessage
      }));
    } else {
      setErrors((prevState) => ({
        ...prevState,
        phoneError: ''
      }));
    }
  };

  const handleOnDateChange = (key, event, value) => {
    let formattedValue = value;
    if (value) {
      formattedValue = new Date(value).toLocaleDateString(
        'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }
      );
    }
    if (key === 'estimatedStartDate') handleEstimatedStartDateValidation(formattedValue);
    if (key === 'estimatedEndDate') handleEstimatedEndDateValidation(formattedValue);
    setProjectInformation((prevState) => ({
      ...prevState,
      [key]: formattedValue
    }));
  };

  const handleOnChange = (key, event) => {
    const { value } = event.target;
    if (key === 'deliveryInstructions' || key === 'siteConditions') {
      setDeliveryPreferences((prevState) => ({
        ...prevState,
        [key]: value
      }));
    } else if (key === 'name' || key === 'phone') {
      setContacts((prevState) => ({
        ...prevState,
        [key]: value
      }));
    } else {
      setProjectInformation((prevState) => ({
        ...prevState,
        [key]: value
      }));
    }
  };

  const handleOnPropertyChange = (key, event) => {
    const { value } = event.target;
    setPropertyTypeDropdownValue(value);
    if (value === 'Other') {
      setProjectInformation((prevState) => ({
        ...prevState,
        propertyType: ''
      }));
    } else {
      setProjectInformation((prevState) => ({
        ...prevState,
        [key]: value
      }));
    }
  };

  useEffect(() => {
    const errorMessageValues = Object.values(errors);
    const hasErrors = errorMessageValues.some(Boolean);
    if (hasErrors) {
      setIsProjectInfoError(true);
    } else {
      setIsProjectInfoError(false);
    }
  }, [errors]);

  useEffect(() => {
    if (showErrors && !projectName) {
      setErrors((prevSate) => ({
        ...prevSate,
        projectNameError: EMPTY_PROJECT_NAME_MESSAGE
      }));
    }
  }, [showErrors]);

  useEffect(() => {
    if (!open && isProjectInfoError) {
      setErrors((prevState) => ({
        ...prevState,
        projectNameError: '',
        propertyTypeError: '',
        estimatedEndDateError: '',
        notesError: '',
      }));
    }
  }, [open]);

  return (
    <>
      <TextField
        required
        ref={projectNameRef}
        id="project-name"
        label="Project Name"
        type="text"
        onBlur={() => { handleNameValidation(projectName); }}
        value={projectName || ''}
        fullWidth
        onChange={(event) => { handleOnChange('projectName', event); }}
        disabled={hasTradeCreditAttached}
        status={
          projectNameError || duplicateNameError ? 'error' : null
        }
        statusMessage={
          projectNameError || duplicateNameError
        }
        InputProps={{
          inputProps: {
            'data-testid': 'project-name-input'
          }
        }}
      />
      {open ? 
      <>
      <FormController fullWidth>
        <FormLabel
          optional
          htmlFor="project-type-form-dropdown"
          id="project-type-form-label"
        >
          Project Type
        </FormLabel>
        <Dropdown
          maxItems={4}
          id="project-type-form-dropdown"
          data-testid="project-type-dropdown"
          value={propertyTypeDropdownValue || ''}
          onBlur={() => { handleTypeValidation(propertyType); }}
          onChange={(event) => { handleOnPropertyChange('propertyType', event); }}
          placeholder="Select a Project Type"
        >
          <MenuItem
            value="Single Family"
            data-testid="single-family"
          >Single Family
          </MenuItem>
          <MenuItem
            value="Multi-Family"
            data-testid="multi-family"
          >Multi-Family
          </MenuItem>
          <MenuItem
            value="Renovation/Remodel"
            data-testid="renovation-remodel"
          >Renovation/Remodel
          </MenuItem>
          <MenuItem
            value="Other"
            data-testid="type-other"
          >Other
          </MenuItem>
        </Dropdown>
        {propertyTypeDropdownValue === 'Other' && (
          <TextField
            fullWidth
            ref={propertyTypeRef}
            id="project-type"
            placeholder="Project Type"
            value={propertyType || ''}
            onBlur={() => { handleOtherTypeValidation(propertyType); }}
            onChange={(event) => { handleOnChange('propertyType', event); }}
            status={
              showErrors && propertyTypeError ? 'error' : null
            }
            statusMessage={
              showErrors ? propertyTypeError : ''
            }
            InputProps={{
              inputProps: {
                'data-testid': 'project-type-input'
              }
            }}
          />
        )}
      </FormController>
      <div>
        <div className="sui-mb-6">
          <DatePicker
            dateFieldProps={
              {
                label: 'Estimated Start Date',
                optional: 'true'
              }
            }
            collapsible
            value={estimatedStartDate || ''}
            onChange={(event, value) => { handleOnDateChange('estimatedStartDate', event, value); }}
            id="project-start-date"
            data-testid="project-start-date-input"
          />
        </div>
        <div className="sui-mb-6">
          <DatePicker
            ref={estimatedEndDateRef}
            dateFieldProps={
              {
                label: 'Estimated End Date',
                optional: 'true'
              }
            }
            collapsible
            value={estimatedEndDate || ''}
            onChange={(event, value) => { handleOnDateChange('estimatedEndDate', event, value); }}
            id="project-end-date"
            data-testid="project-end-date-input"
            status={
              showErrors && estimatedEndDateError ? 'error' : null
            }
            statusMessage={
              showErrors ? estimatedEndDateError : ''
            }
          />
        </div>
        <TextField
          fullWidth
          ref={notesRef}
          optional
          id="project-description"
          label="Project Description"
          placeholder=""
          multiline
          minRows={2}
          maxRows={2}
          value={notes || ''}
          onBlur={() => { handleNotesValidation(notes); }}
          onChange={(event) => { handleOnChange('notes', event); }}
          status={
            showErrors && notesError ? 'error' : null
          }
          statusMessage={
            showErrors ? notesError : ''
          }
          InputProps={{
            inputProps: {
              'data-testid': 'project-description-input'
            }
          }}
        />
      </div>
      <div className="sui-mb-1">
        <TextField
          fullWidth
          ref={siteConditionsRef}
          optional
          id="jobsite-conditions"
          label="Jobsite Conditions"
          placeholder=""
          value={siteConditions || ''}
          onBlur={() => { handleSiteConditionsValidation(siteConditions); }}
          onChange={(event) => { handleOnChange('siteConditions', event); }}
          status={
            showErrors && siteConditionsError ? 'error' : null
          }
          statusMessage={
            showErrors ? siteConditionsError : ''
          }
          InputProps={{
            inputProps: {
              'data-testid': 'jobsite-conditions-input'
            }
          }}
        />
      </div>
      <div className="sui-mb-1">
        <TextField
          fullWidth
          ref={deliveryInstructionsRef}
          optional
          id="delivery-instructions"
          label="Delivery Instructions"
          placeholder=""
          value={deliveryInstructions || ''}
          onBlur={() => { handleDeliveryInstructionsValidation(deliveryInstructions); }}
          onChange={(event) => { handleOnChange('deliveryInstructions', event); }}
          status={
            showErrors && deliveryInstructionsError ? 'error' : null
          }
          statusMessage={
            showErrors ? deliveryInstructionsError : ''
          }
          InputProps={{
            inputProps: {
              'data-testid': 'delivery-instructions-input'
            }
          }}
        />
      </div>
      </>
        : <Accordion expanded={expandedAccordion} onChange={handleChangeAccordion}>
        <AccordionHeader aria-controls="accordion1-content" id="accordion1-header">
          <div className="sui-flex-auto sui-block">
            <Typography variant="h6">Project Details (Optional)</Typography>
            <div className="sui-mt-1">
              <Typography variant="body-base" color="subtle">
                Adding these details will help us show you accurate inventory and delivery times
              </Typography>
            </div>
          </div>
        </AccordionHeader>
        <AccordionBody>
          <div className="sui-grid sui-gap-6">
          <FormController fullWidth>
            <FormLabel
              optional
              htmlFor="project-type-form-dropdown"
              id="project-type-form-label"
        >
          Project Type
        </FormLabel>
        <Dropdown
          maxItems={4}
          id="project-type-form-dropdown"
          data-testid="project-type-dropdown"
          value={propertyTypeDropdownValue || ''}
          onBlur={() => { handleTypeValidation(propertyType); }}
          onChange={(event) => { handleOnPropertyChange('propertyType', event); }}
          placeholder="Select a Project Type"
        >
          <MenuItem
            value="Single Family"
            data-testid="single-family"
          >Single Family
          </MenuItem>
          <MenuItem
            value="Multi-Family"
            data-testid="multi-family"
          >Multi-Family
          </MenuItem>
          <MenuItem
            value="Renovation/Remodel"
            data-testid="renovation-remodel"
          >Renovation/Remodel
          </MenuItem>
          <MenuItem
            value="Other"
            data-testid="type-other"
          >Other
          </MenuItem>
        </Dropdown>
        {propertyTypeDropdownValue === 'Other' && (
          <TextField
            fullWidth
            ref={propertyTypeRef}
            id="project-type"
            placeholder="Project Type"
            value={propertyType || ''}
            onBlur={() => { handleOtherTypeValidation(propertyType); }}
            onChange={(event) => { handleOnChange('propertyType', event); }}
            status={
              showErrors && propertyTypeError ? 'error' : null
            }
            statusMessage={
              showErrors ? propertyTypeError : ''
            }
            InputProps={{
              inputProps: {
                'data-testid': 'project-type-input'
              }
            }}
          />
        )}
      </FormController>
      <div className="sui-grid sui-gap-2">
        <DatePicker
          dateFieldProps={
            {
              label: 'Estimated Start Date',
              optional: 'true'
            }
          }
          collapsible
          value={estimatedStartDate || ''}
          onChange={(event, value) => { handleOnDateChange('estimatedStartDate', event, value); }}
          id="project-start-date"
          data-testid="project-start-date-input"
        />
        <DatePicker
          ref={estimatedEndDateRef}
          dateFieldProps={
            {
              label: 'Estimated End Date',
              optional: 'true'
            }
          }
          collapsible
          value={estimatedEndDate || ''}
          onChange={(event, value) => { handleOnDateChange('estimatedEndDate', event, value); }}
          id="project-end-date"
          data-testid="project-end-date-input"
          status={
            showErrors && estimatedEndDateError ? 'error' : null
          }
          statusMessage={
            showErrors ? estimatedEndDateError : ''
          }
        />
        <TextField
          fullWidth
          ref={notesRef}
          optional
          id="project-description"
          label="Project Description"
          placeholder=""
          multiline
          minRows={2}
          maxRows={2}
          value={notes || ''}
          onBlur={() => { handleNotesValidation(notes); }}
          onChange={(event) => { handleOnChange('notes', event); }}
          status={
            showErrors && notesError ? 'error' : null
          }
          statusMessage={
            showErrors ? notesError : ''
          }
          InputProps={{
            inputProps: {
              'data-testid': 'project-description-input'
            }
          }}
        />
      </div>
      <TextField
        fullWidth
        ref={siteConditionsRef}
        optional
        id="jobsite-conditions"
        label="Jobsite Conditions"
        placeholder=""
        value={siteConditions || ''}
        onBlur={() => { handleSiteConditionsValidation(siteConditions); }}
        onChange={(event) => { handleOnChange('siteConditions', event); }}
        status={
          showErrors && siteConditionsError ? 'error' : null
        }
        statusMessage={
          showErrors ? siteConditionsError : ''
        }
        InputProps={{
          inputProps: {
            'data-testid': 'jobsite-conditions-input'
          }
        }}
      />
      <TextField
        fullWidth
        ref={deliveryInstructionsRef}
        optional
        id="delivery-instructions"
        label="Delivery Instructions"
        placeholder=""
        value={deliveryInstructions || ''}
        onBlur={() => { handleDeliveryInstructionsValidation(deliveryInstructions); }}
        onChange={(event) => { handleOnChange('deliveryInstructions', event); }}
        status={
          showErrors && deliveryInstructionsError ? 'error' : null
        }
        statusMessage={
          showErrors ? deliveryInstructionsError : ''
        }
        InputProps={{
          inputProps: {
            'data-testid': 'delivery-instructions-input'
          }
        }}
      />
      <div>
        <Typography variant="body-lg" weight="bold" className="sui-pt-8 sui-pb-4">
          Jobsite Information
        </Typography>
        <JobsiteInformationForm
          showErrors={showErrors}
          jobsiteInformation={jobsiteInformation}
          setJobsiteInformation={setJobsiteInformation}
          isJobsiteError={isJobsiteError}
          setIsJobsiteError={setIsJobsiteError}
          open={open}
        />
      </div>
          </div>
        </AccordionBody>
      </Accordion>}
    </>
  );
};

ProjectInformationForm.propTypes = {
  showErrors: bool.isRequired,
  projectInformation: shape({
    projectName: string,
    propertyType: string,
    otherPropertyType: string,
    estimatedStartDate: string,
    estimatedEndDate: string,
    notes: string
  }).isRequired,
  deliveryPreferences: shape({
    siteConditions: string,
    deliveryInstructions: string,
  }).isRequired,
  contacts: shape({
    name: string,
    phone: string
  }).isRequired,
  setProjectInformation: func.isRequired,
  isProjectInfoError: bool.isRequired,
  setIsProjectInfoError: func.isRequired,
  open: bool.isRequired,
  duplicateNameError: string.isRequired,
  hasTradeCreditAttached: bool,
  setDeliveryPreferences: func.isRequired,
  setContacts: func.isRequired,
  jobsiteInformation: shape({
    addressLine1: string,
    addressLine2: string,
    city: string,
    state: string,
    zipCode: string,
    country: string,
  }).isRequired,
  setJobsiteInformation: func.isRequired,
  isJobsiteError: bool.isRequired,
  setIsJobsiteError: func.isRequired
};

ProjectInformationForm.defaultProps = {
  hasTradeCreditAttached: false
};

ProjectInformationForm.dataModel = extend(
  JobsiteInformationForm
);